/**
 * Advertiser rate card — single source of truth for /advertise pricing.
 *
 * Geo-priced flat monthly (30-day) sponsorships. Indonesia (the 96%-majority
 * audience) pays affordable IDR; the thinner global remote audience pays a
 * little more in USD. Priced on audience quality, not CPM — display inventory
 * is modest (~6.5k pv/mo), so the numbers are kept low enough to actually sell.
 *
 * Phase 1 renders this in <AdvertiseRateCard>. Phase 2's Mayar self-serve
 * checkout reuses `idrMonthly` for the IDR rail, so keep IDR amounts only here.
 *
 * `usdMonthly` is DERIVED from `ADVERTISER_USD_MINOR_BY_PLACEMENT`
 * (`src/lib/billing/usd-catalog.ts`) — the same numbers the global Gumroad
 * rail actually charges (`ADVERTISER_USD_MINOR` in `catalog-onetime-global.ts`).
 * Never hardcode a separate USD figure here; the rate card and the Gumroad
 * charge would drift (incident 2026-07-12: this page quoted $79/$59/$49/$149
 * while Gumroad charged $69/$49/$39/$129).
 */

import {
	ADVERTISER_FLAT_USD_MINOR,
	ADVERTISER_USD_MINOR_BY_PLACEMENT,
} from "@/lib/billing/usd-catalog";

export type AdvertisePlacementKey = "sidebar" | "blog" | "category" | "bundle";

/** @deprecated legacy SKUs — kept so existing orders resolve */
export interface AdvertisePlan {
	key: AdvertisePlacementKey;
	/** Monthly price in Indonesian rupiah (whole rupiah, not minor units). */
	idrMonthly: number;
	/** Monthly price in US dollars (whole dollars). */
	usdMonthly: number;
	name: { en: string; id: string };
	blurb: { en: string; id: string };
	/** True for the recommended/best-value tier (visually highlighted). */
	highlight?: boolean;
}

/** @deprecated legacy SKUs — kept so existing orders resolve */
export const ADVERTISE_PLANS: AdvertisePlan[] = [
	{
		key: "sidebar",
		idrMonthly: 750_000,
		usdMonthly: ADVERTISER_USD_MINOR_BY_PLACEMENT.sidebar / 100,
		name: { en: "Feed Sidebar", id: "Sidebar Feed" },
		blurb: {
			en: "Sticky sidebar on the main job feed — the most-viewed page on the site.",
			id: "Sidebar sticky di feed lowongan utama — halaman paling banyak dilihat.",
		},
	},
	{
		key: "blog",
		idrMonthly: 500_000,
		usdMonthly: ADVERTISER_USD_MINOR_BY_PLACEMENT.blog / 100,
		name: { en: "Blog Sponsor", id: "Sponsor Blog" },
		blurb: {
			en: "Top + bottom of every blog post — engaged readers, high intent.",
			id: "Atas + bawah setiap artikel blog — pembaca terlibat, niat tinggi.",
		},
	},
	{
		key: "category",
		idrMonthly: 400_000,
		usdMonthly: ADVERTISER_USD_MINOR_BY_PLACEMENT.category / 100,
		name: { en: "Category Footer", id: "Footer Kategori" },
		blurb: {
			en: "Bottom of every remote-jobs category page.",
			id: "Bawah setiap halaman kategori remote-jobs.",
		},
	},
	{
		key: "bundle",
		idrMonthly: 1_500_000,
		usdMonthly: ADVERTISER_USD_MINOR_BY_PLACEMENT.bundle / 100,
		name: { en: "Site-wide Bundle", id: "Bundle Seluruh Situs" },
		blurb: {
			en: "Every placement above, plus the job-detail-mid and feed-inline slots — best value, maximum reach.",
			id: "Semua slot di atas, plus slot job-detail-mid dan feed-inline — nilai terbaik, jangkauan maksimal.",
		},
		highlight: true,
	},
];

/** @deprecated legacy SKUs — kept so existing orders resolve */
export const FOUNDING_DISCOUNT = 0.4;
/** @deprecated legacy SKUs — kept so existing orders resolve */
export const FOUNDING_SLOTS = 3;

export function getAdvertisePlan(
	key: AdvertisePlacementKey,
): AdvertisePlan | undefined {
	return ADVERTISE_PLANS.find((p) => p.key === key);
}

export function formatIdr(amount: number): string {
	return `Rp ${amount.toLocaleString("id-ID")}`;
}

export function formatUsd(amount: number): string {
	return `$${amount.toLocaleString("en-US")}`;
}

/**
 * Apply the founding discount and round to a tidy figure for display.
 * `step` keeps IDR tidy (nearest 1,000) and USD whole-dollar (nearest 1).
 */
export function foundingPrice(amount: number, step = 1): number {
	return Math.round((amount * (1 - FOUNDING_DISCOUNT)) / step) * step;
}

export type AdvertiseFlatKey = "weekly" | "monthly";

/**
 * The one self-serve tier sold on /advertise: a flat price, every placement
 * rotates. `usd` derives from `ADVERTISER_FLAT_USD_MINOR` (never hand-type it).
 */
export const ADVERTISE_FLAT_PLANS: Record<
	AdvertiseFlatKey,
	{ idr: number; usd: number; days: number }
> = {
	weekly: {
		idr: 99_000,
		usd: ADVERTISER_FLAT_USD_MINOR.weekly / 100,
		days: 7,
	},
	monthly: {
		idr: 299_000,
		usd: ADVERTISER_FLAT_USD_MINOR.monthly / 100,
		days: 30,
	},
};

/** One B2B price format: 99000 -> "Rp99rb", 1500000 -> "Rp1,5jt". */
export function formatIdrRb(n: number): string {
	if (n >= 1_000_000) {
		const jt = Number((n / 1_000_000).toFixed(2));
		return `Rp${String(jt).replace(".", ",")}jt`;
	}
	return `Rp${Math.round(n / 1_000)}rb`;
}

/**
 * Free launch placement (owner direction 2026-10-07): a 14-day, zero-price
 * campaign offered ahead of the paid tiers while the `advertise-free-placement`
 * PostHog flag is ENABLED. It never reaches a payment provider — the checkout
 * action short-circuits it into a `free_launch` order + `pending_review`
 * campaign. After the 14 days it ends unless the advertiser buys a paid tier.
 */
export const ADVERTISE_FREE_LAUNCH = { idr: 0, usd: 0, days: 14 } as const;

/**
 * PostHog flag that is the SOLE switch for the free launch tier. ENABLED =
 * offered (builder shows it preselected, checkout accepts it); absent or
 * DISABLED = paid only. Read client-side for display and re-checked
 * server-side in `createAdvertiserCheckout`, so the PostHog flag must have
 * runtime "all". Never shadow it with a code constant.
 */
export const ADVERTISE_FREE_PLACEMENT_FLAG = "advertise-free-placement";
